import { capitalize, markRaw } from 'vue';

import { ExternalSegmentTypes } from '@/common/enums/external-segments/external-segment-types.enum';
import IconAudienceSegmentAppUsage from '@/common/icons/audience-segments/AppUsage.vue';
import IconAudienceEducation from '@/common/icons/audience-segments/Education.vue';
import IconAudienceSegmentFashion from '@/common/icons/audience-segments/Fashionistas.vue';
import IconAudienceSegmentInterest from '@/common/icons/audience-segments/Interest.vue';
import IconAudienceSegmentLifestyle from '@/common/icons/audience-segments/Lifestyle.vue';
import IconAudienceEntertainment from '@/common/icons/audience-segments/MovieGoers.vue';
import IconAudienceSegmentPlaceVisitors from '@/common/icons/audience-segments/PlaceVisitors.vue';
import IconAudienceSegmentIntent from '@/common/icons/audience-segments/PurchaseIntent.vue';
import IconAudienceAlwaysOnTrend from '@/common/icons/audience-segments/scentre/AlwaysOnTrend.vue';
import IconAudienceService from '@/common/icons/audience-segments/Service.vue';
import IconAudienceSegmentShoppers from '@/common/icons/audience-segments/Shoppers.vue';
import IconAudienceSegmentPlaceholder from '@/common/icons/audience-segments/TumbUp.vue';
import IconAudienceWellness from '@/common/icons/audience-segments/Wellness.vue';
import {
  IconEpsilonSegmentAuto,
  IconEpsilonSegmentBankingInvesting,
  IconEpsilonSegmentBooksMagazines,
  IconEpsilonSegmentCharitableDonors,
  IconEpsilonSegmentCpg,
  IconEpsilonSegmentCreditDebitCards,
  IconEpsilonSegmentDemographics,
  IconEpsilonSegmentFinancials,
  IconEpsilonSegmentFoodCooking,
  IconEpsilonSegmentFoodDrink,
  IconEpsilonSegmentHealthcare,
  IconEpsilonSegmentHomeRealEstate,
  IconEpsilonSegmentHousehold,
  IconEpsilonSegmentInsurance,
  IconEpsilonSegmentLifestylesInterests,
  IconEpsilonSegmentPolitical,
  IconEpsilonSegmentPurchaseBehaviors,
  IconEpsilonSegmentPurchaseHistory,
  IconEpsilonSegmentRetail,
  IconEpsilonSegmentSports,
  IconEpsilonSegmentTechnologyConnectivity,
  IconEpsilonSegmentTransactional,
  IconEpsilonSegmentTravel,
} from '@/common/icons/epsilon-segments';
import ExternalSegment from '@/common/models/audience/ExternalSegment';
import { sentenceToCamel } from '@/globals';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export default class ExternalSegmentType extends Validatable<ExternalSegmentType, string> {
  constructor(
    public id: string,
    public grouping: string[] = [],
    public segments: ExternalSegment[] = [],
    public total?: number,
  ) {
    super();
  }

  static fromObject(apiObject) {
    return new ExternalSegmentType(apiObject.id, apiObject.grouping, apiObject.segments, apiObject.total);
  }

  get name(): string {
    const idCamelCase = sentenceToCamel(this.id);
    return i18n.global.t(`names.${idCamelCase}`, 1);
  }

  get description(): string {
    return capitalize(i18n.global.t(`messages.${this.id}`));
  }

  get icon() {
    switch (this.id) {
      case ExternalSegmentTypes.AUTO:
        return markRaw(IconEpsilonSegmentAuto);
      case ExternalSegmentTypes.BANKING_INVESTING:
        return markRaw(IconEpsilonSegmentBankingInvesting);
      case ExternalSegmentTypes.BOOKS_MAGAZINES:
        return markRaw(IconEpsilonSegmentBooksMagazines);
      case ExternalSegmentTypes.CHARITABLE_DONORS:
        return markRaw(IconEpsilonSegmentCharitableDonors);
      case ExternalSegmentTypes.CPG:
        return markRaw(IconEpsilonSegmentCpg);
      case ExternalSegmentTypes.CREDIT_DEBIT_CARDS:
        return markRaw(IconEpsilonSegmentCreditDebitCards);
      case ExternalSegmentTypes.DEMOGRAPHICS:
        return markRaw(IconEpsilonSegmentDemographics);
      case ExternalSegmentTypes.FINANCIALS:
        return markRaw(IconEpsilonSegmentFinancials);
      case ExternalSegmentTypes.FOOD_COOKING:
        return markRaw(IconEpsilonSegmentFoodCooking);
      case ExternalSegmentTypes.FOOD_DRINK:
        return markRaw(IconEpsilonSegmentFoodDrink);
      case ExternalSegmentTypes.HEALTHCARE:
        return markRaw(IconEpsilonSegmentHealthcare);
      case ExternalSegmentTypes.HOME_REAL_ESTATE:
        return markRaw(IconEpsilonSegmentHomeRealEstate);
      case ExternalSegmentTypes.HOUSEHOLD:
        return markRaw(IconEpsilonSegmentHousehold);
      case ExternalSegmentTypes.INSURANCE:
        return markRaw(IconEpsilonSegmentInsurance);
      case ExternalSegmentTypes.LIFESTYLES_INTERESTS:
        return markRaw(IconEpsilonSegmentLifestylesInterests);
      case ExternalSegmentTypes.PURCHASE_BEHAVIORS:
        return markRaw(IconEpsilonSegmentPurchaseBehaviors);
      case ExternalSegmentTypes.PURCHASE_HISTORY:
        return markRaw(IconEpsilonSegmentPurchaseHistory);
      case ExternalSegmentTypes.RETAIL:
        return markRaw(IconEpsilonSegmentRetail);
      case ExternalSegmentTypes.SPORTS:
        return markRaw(IconEpsilonSegmentSports);
      case ExternalSegmentTypes.TECHNOLOGY_CONNECTIVITY:
        return markRaw(IconEpsilonSegmentTechnologyConnectivity);
      case ExternalSegmentTypes.TRAVEL:
        return markRaw(IconEpsilonSegmentTravel);
      case ExternalSegmentTypes.BRAND:
        return markRaw(IconAudienceSegmentShoppers);
      case ExternalSegmentTypes.PLACE_VISITOR:
        return markRaw(IconAudienceSegmentPlaceVisitors);
      case ExternalSegmentTypes.LIFESTYLE:
        return markRaw(IconAudienceSegmentLifestyle);
      case ExternalSegmentTypes.INTEREST:
        return markRaw(IconAudienceSegmentInterest);
      case ExternalSegmentTypes.INTENT:
        return markRaw(IconAudienceSegmentIntent);
      case ExternalSegmentTypes.APP_USAGE:
        return markRaw(IconAudienceSegmentAppUsage);
      case ExternalSegmentTypes.POLITICAL:
        return markRaw(IconEpsilonSegmentPolitical);
      case ExternalSegmentTypes.TRANSACTIONAL:
        return markRaw(IconEpsilonSegmentTransactional);
      case ExternalSegmentTypes.SEASONAL:
        return markRaw(IconAudienceAlwaysOnTrend);
      case ExternalSegmentTypes.APPAREL:
        return markRaw(IconAudienceSegmentFashion);
      case ExternalSegmentTypes.ENTERTAINMENT:
        return markRaw(IconAudienceEntertainment);
      case ExternalSegmentTypes.SERVICES:
        return markRaw(IconAudienceService);
      case ExternalSegmentTypes.WELLNESS:
        return markRaw(IconAudienceWellness);
      case ExternalSegmentTypes.EDUCATION:
        return markRaw(IconAudienceEducation);
      default:
        return markRaw(IconAudienceSegmentPlaceholder);
    }
  }

  static get includeTypeParameters(): string[] {
    return ['category'];
  }
}
